import { useAuth } from '@/providers/Auth';
import { APP_ROUTER } from '@/routes/routes';
import { Drawer, Layout, Menu } from 'antd';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  filterNavGroups,
  findGroupKeyForPath,
  findLeafKeyForPath,
  navGroups,
  toAntdMenuItems,
} from './menuItems';

const MOBILE_BREAKPOINT = 768;
const SIDER_WIDTH = 240;
const SIDER_COLLAPSED_WIDTH = 64;

type Props = {
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
  mobileOpen: boolean;
  onMobileOpenChange: (next: boolean) => void;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

export const Sidebar: React.FC<Props> = ({
  collapsed,
  onCollapsedChange,
  mobileOpen,
  onMobileOpenChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { currentUser } = useAuth();
  const groups = useMemo(() => filterNavGroups(navGroups, currentUser), [currentUser]);

  // When the sidebar is collapsed, antd only opens a submenu on hover, so a
  // click on a group icon appears to do nothing. Intercept the title click to
  // expand the rail and open that group instead.
  const items = useMemo(
    () =>
      toAntdMenuItems(groups, (key) => {
        if (!isMobile && collapsed) {
          onCollapsedChange(false);
          setOpenKeys([key]);
        }
      }),
    [groups, isMobile, collapsed, onCollapsedChange],
  );

  const selectedKey = findLeafKeyForPath(groups, location.pathname);
  const activeGroupKey = findGroupKeyForPath(groups, location.pathname);

  const [openKeys, setOpenKeys] = useState<string[]>(
    activeGroupKey ? [activeGroupKey] : [],
  );

  useEffect(() => {
    if (!activeGroupKey) return;
    setOpenKeys((prev) => (prev.includes(activeGroupKey) ? prev : [...prev, activeGroupKey]));
  }, [activeGroupKey]);

  const onClick: React.ComponentProps<typeof Menu>['onClick'] = ({ key }) => {
    navigate(key);
    if (isMobile) onMobileOpenChange(false);
  };

  const Brand = (
    <div
      className={clsx(
        'flex items-center h-header cursor-pointer select-none border-b border-line-subtle',
        collapsed ? 'justify-center' : 'gap-md px-lg',
      )}
      onClick={() => navigate(APP_ROUTER.HOME)}
    >
      <img src='/logo.svg' alt='' className='w-7 h-7' />
      {!collapsed && (
        <span className='text-fg font-semibold text-[16px] tracking-wide'>
          ヒビノエ
        </span>
      )}
    </div>
  );

  const MenuBody = (
    <Menu
      mode='inline'
      items={items}
      onClick={onClick}
      selectedKeys={selectedKey ? [selectedKey] : []}
      openKeys={collapsed ? [] : openKeys}
      onOpenChange={(keys) => setOpenKeys(keys as string[])}
      inlineCollapsed={!isMobile && collapsed}
      style={{ borderInlineEnd: 'none' }}
    />
  );

  if (isMobile) {
    return (
      <Drawer
        placement='left'
        width={SIDER_WIDTH}
        open={mobileOpen}
        onClose={() => onMobileOpenChange(false)}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        {Brand}
        {MenuBody}
      </Drawer>
    );
  }

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapsedChange}
      width={SIDER_WIDTH}
      collapsedWidth={SIDER_COLLAPSED_WIDTH}
      trigger={null}
      theme='light'
      style={{
        borderInlineEnd: '1px solid var(--color-line-subtle)',
        overflow: 'auto',
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      {Brand}
      {MenuBody}
    </Layout.Sider>
  );
};
